import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

export function RegisterForm() {
  const { t } = useTranslation('auth');
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('register.passwordsNoMatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('register.passwordTooShort'));
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, name || undefined);
      setRegistered(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register.registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="p-4 bg-primary-50 border border-primary-200 rounded text-primary-700 text-sm">
        Registration received. You'll get an email when your account is approved - usually within 24 hours.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      <Input
        type="text"
        label={t('register.name')}
        placeholder={t('register.namePlaceholder')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="name"
      />

      <Input
        type="email"
        label={t('register.email')}
        placeholder={t('register.emailPlaceholder')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
      />

      <Input
        type="password"
        label={t('register.password')}
        placeholder={t('register.passwordPlaceholder')}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        autoComplete="new-password"
        helperText={t('register.passwordHelp')}
      />

      <Input
        type="password"
        label={t('register.confirmPassword')}
        placeholder={t('register.confirmPasswordPlaceholder')}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        autoComplete="new-password"
      />

      <Button type="submit" className="w-full" loading={isLoading}>
        {t('register.createButton')}
      </Button>
    </form>
  );
}
